/** /mcp-limit-hits — client MCP **Limit hits** (client MCP plan §7.6 "How we tune"; WPS FE-03,
 *  backend P0-07).
 *
 *  One row per UTC day, tenant and limit: how often the limit was hit, by how many callers, and
 *  when last — so ops can see which limits bite and raise them (a settings change). Rate limits
 *  are counted from the access log (a row links to those throttled MCP calls on External access),
 *  spend ceilings and caps from their audit event; each hit once.
 *
 *  The window always bounds the scan (the access log is partitioned by month): with a tenant
 *  filter the last 7 days by default and at most 31; across every tenant the last 3 days, by
 *  default and at most — the page says so before asking for more, and the answer names the
 *  window it used. Filters live in the URL (tenant and limit preset-covered; the days are not, so a
 *  saved view never freezes a date). At most 1000 rows come back (`truncated`); the grid sorts and
 *  pages them in the browser. Gated by `console_mcp_limits_view` (Ops Admin by default). */
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridColDef,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import * as limitsApi from '@/api/mcpLimitHits';
import {
  LIMIT_KIND_LABELS,
  LIMIT_SCOPE_LABELS,
  LIMIT_SOURCE_LABELS,
  MAX_DAYS_ALL_TENANTS,
  MAX_DAYS_WITH_TENANT,
  mcpLimitHitKeys,
  windowDays,
  type McpLimitHitRow,
} from '@/api/mcpLimitHits';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { accessLogHref, limitActions, tenantGrid } from './mcpLimitLinks';

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const day = (v: string) => (DAY_RE.test(v) ? v : undefined);
/** Limit keys are lower-case words; a malformed one would 400 the page. */
const keyList = (v: string): string | undefined => {
  const keys = v.split(',').filter((x) => /^[a-z0-9_]+$/.test(x));
  return keys.length ? keys.join(',') : undefined;
};

const TENANT: FilterDef = {
  param: 'tenant',
  apiParam: 'tour_operator_id',
  toApi: tenantIdsToApi,
  tenantScope: 'multi',
};
const LIMIT: FilterDef = { param: 'limit', toApi: keyList };
const FILTERS: FilterDef[] = [
  TENANT,
  LIMIT,
  { param: 'since', toApi: day },
  { param: 'until', toApi: day },
];
/** The days are not preset-covered: a saved view must not freeze a date. */
const PRESET_FILTERS = makeResourceFiltersAdapter([TENANT, LIMIT]);

/** Column order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'day',
  'tenant',
  'name',
  'kind',
  'scope',
  'hits',
  'principals',
  'last_at',
  'source',
];
const DEFAULT_HIDDEN = ['scope'];

const rowId = (r: McpLimitHitRow) => `${r.day}:${r.tenant_id ?? 'none'}:${r.limit}`;
const todayUtc = () => new Date().toISOString().slice(0, 10);
const count = new Intl.NumberFormat('en-US');

/** What a refused request says: the 400's field message or detail, else the transport error. */
function refusalText(e: unknown): string | null {
  if (!(e instanceof ConsoleApiError) || isStatementTimeout(e)) return null;
  const body = (e.body ?? {}) as Record<string, unknown>;
  for (const key of ['detail', 'since', 'until', 'limit', 'tour_operator_id', 'non_field_errors']) {
    const v = body[key];
    if (typeof v === 'string' && v) return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  return e.message;
}

export function McpLimitHitsPage() {
  const apiRef = useGridApiRef();
  const presets = useGridPresets({
    grid: 'mcp_limit_hits',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    filters: PRESET_FILTERS,
    // Rows come back whole (≤ 1000) and sort in the browser: no server ordering to keep.
    supportsSort: false,
  });
  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const since = getParam('since') ?? '';
  const until = getParam('until') ?? '';
  const limits = (getParam('limit') ?? '').split(',').filter(Boolean);

  // The backend's window rule, said before asking: across every tenant at most 3 days.
  const maxDays = pageTenants.length ? MAX_DAYS_WITH_TENANT : MAX_DAYS_ALL_TENANTS;
  const days = since ? windowDays(since, until || todayUtc()) : null;
  const windowProblem =
    days !== null && days < 1
      ? 'The first day is after the last one.'
      : days !== null && days > maxDays
        ? pageTenants.length
          ? `A window is at most ${MAX_DAYS_WITH_TENANT} days; this one is ${days}.`
          : `Across every tenant a window is at most ${MAX_DAYS_ALL_TENANTS} days; this one is ${days}. Pick a tenant for up to ${MAX_DAYS_WITH_TENANT} days.`
        : null;

  const [extendedTimeout, setExtendedTimeout] = useState(false);
  const params = extra as limitsApi.McpLimitHitsParams;
  const identity = JSON.stringify(params);
  useEffect(() => setExtendedTimeout(false), [identity]);

  const autoRefresh = useGridAutoRefresh({ grid: 'mcp_limit_hits' });
  const query = useQuery({
    queryKey: [...mcpLimitHitKeys.hits(params), extendedTimeout],
    queryFn: () =>
      limitsApi.limitHits({
        ...params,
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking && windowProblem === null,
  });
  const data = windowProblem === null ? query.data : undefined;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const limitOptions = useMemo(
    () => (query.data?.limits ?? []).map((l) => ({ value: l.key, label: l.name })),
    [query.data],
  );
  const labelOf = (key: string) => limitOptions.find((o) => o.value === key)?.label ?? key;
  const refused = refusalText(query.error);

  const columns = useMemo<GridColDef<McpLimitHitRow>[]>(
    () => [
      { field: 'day', headerName: 'Day (UTC)', width: 120 },
      {
        field: 'tenant',
        headerName: 'Tenant',
        width: 170,
        valueGetter: (_, row) =>
          row.tenant?.code ?? (row.tenant_id === null ? '' : `#${row.tenant_id}`),
        renderCell: (p) =>
          p.row.tenant ? (
            <WithTenantMark environment={p.row.tenant.environment}>
              <ValueActions actions={operatorActions(p.row.tenant, tenantGrid(p.row.tenant.id))}>
                {p.row.tenant.code || p.row.tenant.name}
              </ValueActions>
            </WithTenantMark>
          ) : p.row.tenant_id === null ? (
            <Typography variant="body2" component="span" color="text.secondary">
              No tenant
            </Typography>
          ) : (
            `#${p.row.tenant_id}`
          ),
      },
      {
        field: 'name',
        headerName: 'Limit',
        flex: 1,
        minWidth: 240,
        renderCell: (p) => (
          <ValueActions actions={limitActions(p.row.limit)}>
            {p.row.name}{' '}
            <Typography
              variant="caption"
              component="span"
              color="text.secondary"
              sx={{ fontFamily: 'monospace' }}
            >
              {p.row.limit}
            </Typography>
          </ValueActions>
        ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 130,
        valueGetter: (_, row) => LIMIT_KIND_LABELS[row.kind],
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            color={p.row.kind === 'spend' ? 'warning' : 'default'}
            label={LIMIT_KIND_LABELS[p.row.kind]}
          />
        ),
      },
      {
        field: 'scope',
        headerName: 'Whose allowance',
        width: 150,
        valueGetter: (_, row) => LIMIT_SCOPE_LABELS[row.scope],
      },
      {
        field: 'hits',
        headerName: 'Hits',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) => count.format(value),
      },
      {
        field: 'principals',
        headerName: 'Callers',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) => count.format(value),
      },
      {
        field: 'last_at',
        headerName: 'Last hit',
        width: 160,
        renderCell: (p) => (
          <span title={formatRelativeTime(p.row.last_at)}>{formatDate(p.row.last_at)}</span>
        ),
      },
      {
        field: 'source',
        headerName: 'Counted from',
        width: 150,
        valueGetter: (_, row) => LIMIT_SOURCE_LABELS[row.source],
        renderCell: (p) => {
          const href = accessLogHref(p.row);
          return href ? (
            <Link component={RouterLink} to={href} underline="hover">
              {LIMIT_SOURCE_LABELS[p.row.source]}
            </Link>
          ) : (
            LIMIT_SOURCE_LABELS[p.row.source]
          );
        },
      },
    ],
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'since',
      priority: 5,
      active: Boolean(since),
      node: (
        <TextField
          size="small"
          type="date"
          label="From (UTC)"
          InputLabelProps={{ shrink: true }}
          value={since}
          onChange={(e) => setParam('since', e.target.value || null)}
          sx={{ width: 170 }}
        />
      ),
    },
    {
      key: 'until',
      priority: 5,
      active: Boolean(until),
      node: (
        <TextField
          size="small"
          type="date"
          label="To (UTC)"
          InputLabelProps={{ shrink: true }}
          value={until}
          onChange={(e) => setParam('until', e.target.value || null)}
          sx={{ width: 170 }}
        />
      ),
    },
    {
      key: 'tenant',
      priority: 4,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'limit',
      priority: 3,
      active: limits.length > 0,
      node: (
        <TextField
          select
          size="small"
          label="Limit"
          value={limits}
          onChange={(e) =>
            setParam('limit', (e.target.value as unknown as string[]).join(',') || null)
          }
          sx={{ width: 240 }}
          SelectProps={{
            multiple: true,
            renderValue: (sel) => (sel as string[]).map(labelOf).join(', '),
          }}
        >
          {limitOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Checkbox size="small" checked={limits.includes(o.value)} sx={{ py: 0 }} />
              <ListItemText primary={o.label} secondary={o.value} />
            </MenuItem>
          ))}
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="mcp-limit-hits"
        title="MCP limit hits"
        subtitle="Which client MCP limits clients reach, how often and by how many callers — per UTC day, tenant and limit."
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {windowProblem ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-window-problem>
          {windowProblem}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-window>
          {[
            data &&
              `UTC days ${data.since} to ${data.until} · ${rows.length} row${rows.length === 1 ? '' : 's'}.`,
            pageTenants.length
              ? `With a tenant: the last 7 days by default, at most ${MAX_DAYS_WITH_TENANT}.`
              : `Across every tenant: at most ${MAX_DAYS_ALL_TENANTS} days — pick a tenant for longer.`,
          ]
            .filter(Boolean)
            .join(' ')}
        </Typography>
      )}

      {data?.truncated && (
        <Alert severity="warning" sx={{ mb: 2 }} data-truncated>
          More rows than the 1000 shown — narrow the days, the tenants or the limits.
        </Alert>
      )}

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {refused && windowProblem === null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {refused}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<McpLimitHitRow>
          apiRef={apiRef}
          rows={rows}
          getRowId={rowId}
          columns={orderColumns(columns, presets.orderedFields)}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          pageSizeOptions={[25, 50, 100]}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          disableRowSelectionOnClick
          disableColumnFilter
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {windowProblem
                  ? 'Fix the window to see the limit hits.'
                  : 'No limit was hit in this window.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, {
                paused: extendedTimeout,
                disabled: windowProblem !== null,
              }),
              hideFilterButton: true,
              onResetAllFilters: resetAllFilters,
              resetDisabled: !anyFilterActive,
              exportFileName: 'mcp-limit-hits',
              presets: presets.toolbar,
            } as GridToolbarProps,
          }}
        />
      )}
    </Box>
  );
}
